import React, { Component, Suspense, lazy, useEffect } from 'react';
import { Routes, Route, useLocation, Link } from 'react-router-dom';
import { HelmetProvider } from 'react-helmet-async';
import Navbar from './components/Navbar';
import Footer from './components/Footer';
import AnalyticsTracker from './components/AnalyticsTracker';
import SitewideSeo from './components/SitewideSeo';
import ScrollProgress from './components/ScrollProgress';
import MailchimpPopup from './components/MailchimpPopup';
import PageLoadingSkeleton from './components/PageLoadingSkeleton';

const ROUTE_LABELS = {
  '/': 'home page',
  '/paid-restrooms': 'paid restrooms page',
  '/pilot': 'pilot program page',
  '/ambassador': 'ambassador program page',
  '/refer': 'business referral application page',
  '/contact': 'contact page',
  '/investors': 'investor inquiries page',
  '/privacy': 'privacy policy page',
  '/terms': 'terms page',
  '/support': 'support page',
  '/schedule': 'meeting scheduler',
  '/c': 'access code page',
};

let aosModule;

const Home = lazy(() => import('./pages/Home'));
const PaidRestrooms = lazy(() => import('./pages/PaidRestrooms'));
const Pilot = lazy(() => import('./pages/Pilot'));
const Ambassador = lazy(() => import('./pages/Ambassador'));
const Referral = lazy(() => import('./pages/Referral'));
const Contact = lazy(() => import('./pages/Contact'));
const Investors = lazy(() => import('./pages/Investors'));
const Privacy = lazy(() => import('./pages/Privacy'));
const Terms = lazy(() => import('./pages/Terms'));
const Support = lazy(() => import('./pages/Support'));
const Schedule = lazy(() => import('./pages/Schedule'));
const Claim = lazy(() => import('./pages/Claim'));
const NotFound = lazy(() => import('./pages/NotFound'));

class RouteErrorBoundary extends Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false };
  }

  static getDerivedStateFromError() {
    return { hasError: true };
  }

  componentDidCatch(error, info) {
    console.error(error, info);
  }

  componentDidUpdate(prevProps) {
    if (this.state.hasError && prevProps.locationKey !== this.props.locationKey) {
      this.setState({ hasError: false });
    }
  }

  render() {
    if (this.state.hasError) {
      return (
        <div className="fl-route-error" role="alert">
          <h1 className="fl-route-error__title">This page did not load.</h1>
          <p className="fl-route-error__copy">Try again, or head back to the homepage.</p>
          <div className="fl-route-error__actions">
            <button className="fl-btn fl-btn--primary" type="button" onClick={() => this.setState({ hasError: false })}>
              Try again
            </button>
            <Link className="fl-btn fl-btn--secondary" to="/">Home</Link>
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}

function ScrollToTop() {
  const { pathname } = useLocation();
  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'instant' });
    requestAnimationFrame(() => {
      document.getElementById('main-content')?.focus({ preventScroll: true });
    });
    aosModule?.refreshHard?.();
  }, [pathname]);
  return null;
}

function RouteAnnouncer() {
  const { pathname } = useLocation();
  return (
    <div className="sr-only" role="status" aria-live="polite" aria-atomic="true">
      Loaded {ROUTE_LABELS[pathname] ?? 'page'}
    </div>
  );
}

function RouteContent() {
  const { pathname } = useLocation();
  return (
    <div key={pathname} className="fl-route-shell">
      <Routes>
        <Route path="/" element={<Home />} />
        <Route path="/paid-restrooms" element={<PaidRestrooms />} />
        <Route path="/pilot" element={<Pilot />} />
        <Route path="/ambassador" element={<Ambassador />} />
        <Route path="/ambassador/:code" element={<Ambassador />} />
        <Route path="/refer" element={<Referral />} />
        <Route path="/refer/:code" element={<Referral />} />
        <Route path="/contact" element={<Contact />} />
        <Route path="/investors" element={<Investors />} />
        <Route path="/privacy" element={<Privacy />} />
        <Route path="/terms" element={<Terms />} />
        <Route path="/support" element={<Support />} />
        <Route path="/schedule" element={<Schedule />} />
        <Route path="/c" element={<Claim />} />
        <Route path="/c/:slug" element={<Claim />} />
        <Route path="/dashboard" element={<ExternalDashboardRedirect />} />
        <Route path="/admin" element={<ExternalDashboardRedirect />} />
        <Route path="*" element={<NotFound />} />
      </Routes>
    </div>
  );
}

function RouteBoundary({ children }) {
  const location = useLocation();
  return (
    <RouteErrorBoundary locationKey={location.key}>
      {children}
    </RouteErrorBoundary>
  );
}

function ExternalDashboardRedirect() {
  React.useEffect(() => {
    window.location.href = 'https://dashboard.paidrestrooms.com';
  }, []);
  return null;
}

function Loading() {
  const { pathname } = useLocation();
  return <PageLoadingSkeleton pageLabel={ROUTE_LABELS[pathname] ?? 'page'} />;
}

export default function App() {
  useEffect(() => {
    document.documentElement.setAttribute('data-theme', 'light');
    if (window.matchMedia?.('(prefers-reduced-motion: reduce)').matches) return;
    let cancelled = false;
    import('aos').then((module) => {
      if (cancelled) return;
      aosModule = module.default ?? module;
      aosModule.init({ duration: 700, once: true, easing: 'ease-out-cubic', offset: 80 });
    });
    return () => { cancelled = true; };
  }, []);

  return (
    <HelmetProvider>
      <div style={{ display: 'flex', flexDirection: 'column', minHeight: '100dvh' }}>
          <a href="#main-content" className="fl-skip-link">Skip to main content</a>
          <ScrollProgress />
          <ScrollToTop />
          <RouteAnnouncer />
          <SitewideSeo />
          <AnalyticsTracker />
          <MailchimpPopup />
          <Navbar />
          <main id="main-content" tabIndex={-1} style={{ flex: 1 }}>
            <RouteBoundary>
              <Suspense fallback={<Loading />}>
                <RouteContent />
              </Suspense>
            </RouteBoundary>
          </main>
        <Footer />
      </div>
    </HelmetProvider>
  );
}
